import { track } from '@/lib/analytics';
import { useShowcase } from '@/hooks/use-showcase';
import { AnalyticsEvents } from '@beacon/analytics-events';
import { Button, Dialog, DialogContent, DialogTitle } from '@beacon/ui/components';
import { useRef, useState } from 'react';
import { onboardingBuild, onboardingIntro } from './onboarding-content';

const KEY = 'first-login-onboarding';
const ORANGE = 'var(--color-ds-orange-9)';

export function FirstLoginOnboarding() {
  const { shouldShow, complete } = useShowcase(KEY);

  if (!shouldShow) return null;

  return <Walkthrough onComplete={complete} />;
}

function Walkthrough({ onComplete }: { onComplete: () => void }) {
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);

  const isIntro = step === 0;

  const started = useRef(false);

  if (!started.current) {
    started.current = true;
    track(AnalyticsEvents.SHOWCASE_STARTED, { key: KEY });
    track(AnalyticsEvents.SHOWCASE_STEP_VIEWED, { key: KEY, step: 0 });
  }

  const dismiss = (via: 'finish' | 'skip' | 'dismiss') => {
    setOpen(false);
    track(AnalyticsEvents.SHOWCASE_COMPLETED, { key: KEY, via, stepsSeen: step + 1 });
    onComplete();
  };

  const goTo = (next: number) => {
    setStep(next);
    track(AnalyticsEvents.SHOWCASE_STEP_VIEWED, { key: KEY, step: next });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss('dismiss');
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        style={{ width: isIntro ? 560 : 720, maxWidth: 'calc(100vw - 32px)' }}
      >
        <DialogTitle className="sr-only">Welcome to Beacon</DialogTitle>

        <div>{isIntro ? onboardingIntro : onboardingBuild}</div>

        <div className="border-ds-stroke-primary flex items-center justify-between gap-3 border-t px-4 py-3 sm:px-5 sm:py-3.5">
          <div className="flex items-center gap-1.5">
            {[0, 1].map((k) => (
              <span
                key={k}
                style={{
                  width: k === step ? 16 : 6,
                  height: 6,
                  borderRadius: 99,
                  background: k === step ? ORANGE : 'var(--color-ds-neutral-a5)',
                }}
              />
            ))}
            <span className="text-ds-text-tertiary text-3xs ml-2 font-mono">{`Step ${step + 1} of 2`}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" mono onClick={() => (isIntro ? dismiss('skip') : goTo(0))}>
              {isIntro ? 'SKIP' : 'BACK'}
            </Button>
            <Button size="sm" mono iconRight="arrow-right-line" onClick={() => (isIntro ? goTo(1) : dismiss('finish'))}>
              {isIntro ? 'SHOW ME HOW' : 'GET STARTED'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
